import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as tus from "tus-js-client";
import { bytesToSize } from "../../stores/utils";

const MAX_CONCURRENT_UPLOADS = 3;

const UPLOAD_STATUS = Object.freeze({
  QUEUED: "queued",
  UPLOADING: "uploading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
});

const createEntryId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const fileIdentity = (file) =>
  `${file.name}\u0000${file.size}\u0000${file.lastModified}`;

const fileMatchesAccept = (file, accept) => {
  if (!accept || accept === "*/*") {
    return true;
  }

  const normalizedType = (file.type || "").toLowerCase();
  const normalizedName = file.name.toLowerCase();
  return accept
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => {
      if (rule.startsWith(".")) {
        return normalizedName.endsWith(rule);
      }
      if (rule.endsWith("/*")) {
        return normalizedType.startsWith(rule.slice(0, -1));
      }
      return normalizedType === rule;
    });
};

export default function TusJsClientWrapper({
  hideFinished = false,
  onSuccess = () => {},
  onUploadsChange,
  onStatusChange = () => {},
  metadata = {},
  asCamera = false,
  accept = "image/*",
  maxFiles = Number.POSITIVE_INFINITY,
  maxFileSize = Number.POSITIVE_INFINITY,
  maxTotalSize = Number.POSITIVE_INFINITY,
  formatLabel = "",
}) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState({});
  const [isDragActive, setIsDragActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const entriesRef = useRef({});
  const uploadsRef = useRef({});
  const queueRef = useRef([]);
  const activeCountRef = useRef(0);
  const mountedRef = useRef(true);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  const lastStatusRef = useRef({ isBusy: false, hasErrors: false });

  const metadataRef = useRef(metadata);
  const onSuccessRef = useRef(onSuccess);
  const onUploadsChangeRef = useRef(onUploadsChange);
  const onStatusChangeRef = useRef(onStatusChange);
  metadataRef.current = metadata;
  onSuccessRef.current = onSuccess;
  onUploadsChangeRef.current = onUploadsChange;
  onStatusChangeRef.current = onStatusChange;

  const emitStatus = () => {
    const currentEntries = Object.values(entriesRef.current);
    const nextStatus = {
      isBusy: currentEntries.some((entry) =>
        [UPLOAD_STATUS.QUEUED, UPLOAD_STATUS.UPLOADING].includes(entry.status),
      ),
      hasErrors: currentEntries.some(
        (entry) => entry.status === UPLOAD_STATUS.FAILED,
      ),
    };
    const previousStatus = lastStatusRef.current;
    if (
      nextStatus.isBusy !== previousStatus.isBusy ||
      nextStatus.hasErrors !== previousStatus.hasErrors
    ) {
      lastStatusRef.current = nextStatus;
      onStatusChangeRef.current(nextStatus);
    }
  };

  const emitUploads = () => {
    if (!onUploadsChangeRef.current) {
      return;
    }
    const completedUploads = Object.values(entriesRef.current)
      .filter((entry) => entry.status === UPLOAD_STATUS.SUCCEEDED)
      .map((entry) => entry.upload);
    onUploadsChangeRef.current(completedUploads);
  };

  const publishEntries = ({ uploadsChanged = false } = {}) => {
    if (mountedRef.current) {
      setEntries({ ...entriesRef.current });
    }
    if (uploadsChanged) {
      emitUploads();
    }
    emitStatus();
  };

  const finishActiveUpload = (entryId, upload) => {
    if (uploadsRef.current[entryId] !== upload) {
      return false;
    }
    delete uploadsRef.current[entryId];
    activeCountRef.current = Math.max(0, activeCountRef.current - 1);
    return true;
  };

  const processQueue = () => {
    while (
      activeCountRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const entryId = queueRef.current.shift();
      const entry = entriesRef.current[entryId];
      if (!entry || entry.status !== UPLOAD_STATUS.QUEUED) {
        continue;
      }

      activeCountRef.current += 1;
      entry.status = UPLOAD_STATUS.UPLOADING;
      publishEntries();

      const upload = new tus.Upload(entry.file, {
        endpoint: "/v1/uploads/",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        removeFingerprintOnSuccess: true,
        uploadDataDuringCreation: false,
        retryDelays: [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000],
        metadata: {
          filename: entry.file.name,
          filetype: entry.file.type,
          ...metadataRef.current,
        },
        onError: () => {
          if (!finishActiveUpload(entryId, upload)) {
            return;
          }
          const currentEntry = entriesRef.current[entryId];
          if (currentEntry) {
            currentEntry.status = UPLOAD_STATUS.FAILED;
            currentEntry.error = true;
          }
          publishEntries();
          processQueue();
        },
        onProgress: (uploadedBytes, totalBytes) => {
          const currentEntry = entriesRef.current[entryId];
          if (!currentEntry || uploadsRef.current[entryId] !== upload) {
            return;
          }
          currentEntry.uploadedBytes = uploadedBytes;
          currentEntry.totalBytes = totalBytes;
          currentEntry.progress = totalBytes
            ? (uploadedBytes / totalBytes) * 100
            : 0;
          publishEntries();
        },
        onSuccess: () => {
          if (!finishActiveUpload(entryId, upload)) {
            return;
          }
          const currentEntry = entriesRef.current[entryId];
          if (!currentEntry) {
            processQueue();
            return;
          }
          if (!upload.hash || !upload.url) {
            currentEntry.status = UPLOAD_STATUS.FAILED;
            currentEntry.error = true;
            publishEntries();
            processQueue();
            return;
          }

          const completedUpload = {
            url: upload.url,
            hash: upload.hash,
            name: entry.file.name,
            size: entry.file.size,
            type: entry.file.type,
          };
          currentEntry.status = UPLOAD_STATUS.SUCCEEDED;
          currentEntry.uploadedBytes = entry.file.size;
          currentEntry.totalBytes = entry.file.size;
          currentEntry.progress = 100;
          currentEntry.upload = completedUpload;
          currentEntry.error = false;
          onSuccessRef.current(completedUpload);
          publishEntries({ uploadsChanged: true });
          processQueue();
        },
        onShouldRetry: (error) => {
          const status = error.originalResponse
            ? error.originalResponse.getStatus()
            : 0;
          return ![401, 403, 404, 413].includes(status);
        },
        onAfterResponse: (request, response) => {
          const hash = response.getHeader("X-Content-Hash");
          if (hash) {
            Object.assign(upload, { hash });
          }
        },
      });

      uploadsRef.current[entryId] = upload;
      upload
        .findPreviousUploads()
        .then((previousUploads) => {
          if (uploadsRef.current[entryId] !== upload) {
            return;
          }
          if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0]);
          }
          upload.start();
        })
        .catch(() => {
          if (uploadsRef.current[entryId] === upload) {
            upload.start();
          }
        });
    }
  };

  const addValidationError = (errors, key, values = {}) => {
    if (!errors.some((error) => error.key === key)) {
      errors.push({ id: createEntryId(), key, values });
    }
  };

  const handleUploadFiles = (newFiles) => {
    const nextErrors = [];
    const currentEntries = Object.values(entriesRef.current);
    const identities = new Set(
      currentEntries.map((entry) => fileIdentity(entry.file)),
    );
    let acceptedCount = currentEntries.length;
    let acceptedTotalSize = currentEntries.reduce(
      (total, entry) => total + entry.file.size,
      0,
    );

    Array.from(newFiles).forEach((file) => {
      if (identities.has(fileIdentity(file))) {
        addValidationError(nextErrors, "files.upload_duplicate", {
          name: file.name,
        });
        return;
      }
      if (!fileMatchesAccept(file, accept)) {
        addValidationError(nextErrors, "files.upload_invalid_type", {
          name: file.name,
        });
        return;
      }
      if (file.size <= 0) {
        addValidationError(nextErrors, "files.upload_invalid_size", {
          name: file.name,
        });
        return;
      }
      if (file.size > maxFileSize) {
        addValidationError(nextErrors, "files.upload_too_large", {
          name: file.name,
          maxSize: bytesToSize(maxFileSize),
        });
        return;
      }
      if (acceptedCount >= maxFiles) {
        addValidationError(nextErrors, "files.upload_too_many", {
          maxFiles,
        });
        return;
      }
      if (acceptedTotalSize + file.size > maxTotalSize) {
        addValidationError(nextErrors, "files.upload_total_too_large", {
          maxSize: bytesToSize(maxTotalSize),
        });
        return;
      }

      const entryId = createEntryId();
      entriesRef.current[entryId] = {
        id: entryId,
        file,
        status: UPLOAD_STATUS.QUEUED,
        uploadedBytes: 0,
        totalBytes: file.size,
        progress: 0,
        error: false,
        upload: null,
      };
      queueRef.current.push(entryId);
      identities.add(fileIdentity(file));
      acceptedCount += 1;
      acceptedTotalSize += file.size;
    });

    setValidationErrors(nextErrors);
    publishEntries();
    processQueue();
  };

  const handleFileEvent = (event) => {
    handleUploadFiles(event.target.files);
    event.target.value = "";
  };

  const removeEntry = (entryId) => {
    const entry = entriesRef.current[entryId];
    if (!entry) {
      return;
    }

    queueRef.current = queueRef.current.filter((id) => id !== entryId);
    const upload = uploadsRef.current[entryId];
    if (upload) {
      delete uploadsRef.current[entryId];
      activeCountRef.current = Math.max(0, activeCountRef.current - 1);
      upload.abort().catch(() => {});
    }

    const uploadsChanged = entry.status === UPLOAD_STATUS.SUCCEEDED;
    delete entriesRef.current[entryId];
    publishEntries({ uploadsChanged });
    processQueue();
  };

  const retryEntry = (entryId) => {
    const entry = entriesRef.current[entryId];
    if (!entry || entry.status !== UPLOAD_STATUS.FAILED) {
      return;
    }
    entry.status = UPLOAD_STATUS.QUEUED;
    entry.uploadedBytes = 0;
    entry.progress = 0;
    entry.error = false;
    queueRef.current.push(entryId);
    publishEntries();
    processQueue();
  };

  const resetDragState = () => {
    dragCounterRef.current = 0;
    setIsDragActive(false);
  };

  useEffect(
    () => () => {
      mountedRef.current = false;
      queueRef.current = [];
      Object.values(uploadsRef.current).forEach((upload) => {
        upload.abort().catch(() => {});
      });
      uploadsRef.current = {};
      activeCountRef.current = 0;
    },
    [],
  );

  const visibleEntries = Object.values(entries).filter((entry) =>
    hideFinished ? entry.status !== UPLOAD_STATUS.SUCCEEDED : true,
  );
  const hasFiniteConstraints =
    Number.isFinite(maxFiles) ||
    Number.isFinite(maxFileSize) ||
    Number.isFinite(maxTotalSize);
  const dropAreaClassName = `file-drop-area${
    isDragActive ? " file-drop-area-active" : ""
  }`;

  return (
    <div>
      <input
        type="file"
        hidden
        multiple
        onChange={handleFileEvent}
        accept={accept}
        capture={asCamera}
        ref={fileInputRef}
      />

      <button
        type="button"
        className={dropAreaClassName}
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          dragCounterRef.current += 1;
          setIsDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          event.preventDefault();
          dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
          if (dragCounterRef.current === 0) {
            setIsDragActive(false);
          }
        }}
        onDragEnd={resetDragState}
        onDrop={(event) => {
          event.preventDefault();
          resetDragState();
          if (event.dataTransfer.files.length > 0) {
            handleUploadFiles(event.dataTransfer.files);
          }
        }}
      >
        <i className="fa fa-cloud-arrow-up fa-2x mb-2" aria-hidden="true" />
        <span className="d-block fw-semibold">
          {isDragActive
            ? t("files.drop_active")
            : t("files.drop_hint")}
        </span>
        {hasFiniteConstraints && (
          <span className="d-block small mt-1 text-muted">
            {t("files.upload_constraints", {
              formats: formatLabel,
              maxFileSize: bytesToSize(maxFileSize),
              maxFiles,
              maxTotalSize: bytesToSize(maxTotalSize),
            })}
          </span>
        )}
      </button>

      {validationErrors.map((error) => (
        <div
          key={error.id}
          className="alert alert-warning mt-2 mb-0"
          role="alert"
        >
          {t(error.key, error.values)}
        </div>
      ))}

      {visibleEntries.length > 0 && (
        <ul className="list-unstyled mb-0 mt-3" aria-live="polite">
          {visibleEntries.map((entry) => {
            const isUploading = entry.status === UPLOAD_STATUS.UPLOADING;
            const isQueued = entry.status === UPLOAD_STATUS.QUEUED;
            const isFailed = entry.status === UPLOAD_STATUS.FAILED;
            const isSucceeded = entry.status === UPLOAD_STATUS.SUCCEEDED;
            const isPdf = entry.file.name.toLowerCase().endsWith(".pdf");
            const progress = entry.progress.toFixed(2);

            return (
              <li key={entry.id} className="border-top pt-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                  <i
                    className={`fa ${isPdf ? "fa-file-pdf text-danger" : "fa-file-image text-primary"} fa-fw`}
                    aria-hidden="true"
                  />
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="text-truncate" title={entry.file.name}>
                      {entry.file.name}
                    </div>
                    <div className="small text-muted">
                      {isQueued && t("files.upload_queued")}
                      {isUploading &&
                        t("files.upload_progress", {
                          progress,
                          uploaded: bytesToSize(entry.uploadedBytes),
                          total: bytesToSize(entry.totalBytes),
                        })}
                      {isSucceeded && t("files.upload_ready_for_save")}
                      {isFailed && t("files.upload_failed")}
                    </div>
                  </div>
                  {isFailed && (
                    <button
                      type="button"
                      className="btn btn-sm btn-default"
                      onClick={() => retryEntry(entry.id)}
                    >
                      <i className="fa fa-rotate-right fa-fw me-1" />
                      {t("files.upload_retry")}
                    </button>
                  )}
                  {(!isSucceeded || onUploadsChange) && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title={
                        isUploading || isQueued
                          ? t("files.upload_cancel")
                          : t("files.upload_remove")
                      }
                      aria-label={
                        isUploading || isQueued
                          ? t("files.upload_cancel")
                          : t("files.upload_remove")
                      }
                      onClick={() => removeEntry(entry.id)}
                    >
                      <i className="fa fa-xmark" />
                    </button>
                  )}
                </div>
                {(isUploading || isQueued) && (
                  <div
                    className="progress mt-2"
                    role="progressbar"
                    aria-label={t("files.upload_progress_label", {
                      name: entry.file.name,
                    })}
                    aria-valuenow={Math.round(entry.progress)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    <div
                      className={`progress-bar progress-bar-navy-light${
                        isUploading
                          ? " progress-bar-striped progress-bar-animated"
                          : ""
                      }`}
                      style={{ width: `${entry.progress}%` }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

TusJsClientWrapper.propTypes = {
  hideFinished: PropTypes.bool,
  onSuccess: PropTypes.func,
  onUploadsChange: PropTypes.func,
  onStatusChange: PropTypes.func,
  metadata: PropTypes.object,
  asCamera: PropTypes.bool,
  accept: PropTypes.string,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
  maxTotalSize: PropTypes.number,
  formatLabel: PropTypes.string,
};
